import React, { useEffect, useState } from 'react'
import { Route, Switch, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SlrBookingList from './List/SlrBookingList'
import SlrBookingDetailsView from './View/SlrBookingDetailsView'
import SlrBookingInputs from './Booking/SlrBookingInputs'
import getCurrentUser from '../../utils/getCurrentUser'
import ErrorPage from '../ErrorPage'
import AirportAutocomplete from '../AirportAutocomplete'
import { CustomPopup } from '../../common/Popup/CustomPopup'
import { AUTOCOMPLETE_TYPE, FORM_FIELDS } from '../../constants'
import Messages from '../../constants/Messages'
import sessionKeys from '../../constants/sessionKeys'
import getPrincipal from '../../utils/getPrincipal'
import { clearItemFromLocalStorage, clearItemFromSessionStorage, setToSessionStorage } from '../../utils/StorageUtils'
import { getSlrFallbackStation, hasSlrUserDetails, isEnterpriseOrGsaUser } from './SLRUtils'
import './SlrBookingRoot.scss'

const SlrBookingRoot = () => {
	const userDetails = getCurrentUser()
	const history = useHistory()
	const { t } = useTranslation(['labels', 'errors'])
	const [stationCode, setStationCode] = useState(() => getSlrFallbackStation())
	const [selectedAirport, setSelectedAirport] = useState(null)
	const [stationError, setStationError] = useState(null)
	const hasUserDetails = hasSlrUserDetails(userDetails)
	const canSelectStation = isEnterpriseOrGsaUser()

	useEffect(() => {
		if (hasUserDetails) {
			clearItemFromSessionStorage(sessionKeys.SLR_FALLBACK_STATION)
		}
	}, [hasUserDetails])

	const logout = () => {
		history.push('/logout')
	}

	const proceed = () => {
		if (!selectedAirport?.code || !/^[a-zA-Z]{3}$/.test(selectedAirport.code)) {
			setStationError(Messages.INVALID_ORIGIN)
			return
		}
		setStationError(null)
		try {
			const code = selectedAirport.code.toUpperCase()
			setToSessionStorage(sessionKeys.SLR_FALLBACK_STATION, JSON.stringify({
				userId: getPrincipal()?.sub,
				stationCode: code,
			}))
			clearItemFromLocalStorage(sessionKeys.SLR_LIST_DETAILS)
			setStationCode(code)
			history.replace('/slr-bookings')
		} catch (error) {
			setStationError(Messages.PROCESS_REQUEST_FAILED)
		}
	}

	if (!hasUserDetails && canSelectStation && !stationCode) {
		return (
			<CustomPopup
				isVisible={true}
				className='slr-station-popup'
				headerComponent={{
					mainHeader: (
						t('COMMON.STATION_CODE_REQUIRED')
					),
					subHeader: t('COMMON.STATION_CODE_REQUIRED_DESC')
				}}
				footerPriBtnLabel={t('COMMON.CONT')}
				footerSecBtnLabel={t('HEADER.LOG_OUT')}
				footerPriBtnClick={proceed}
				footerSecBtnClick={logout}
				footerPriBtnDisabled={!selectedAirport?.code}
				onClose={logout}
				closeOnEscape={false}
				dismissableMask={false}
			>
				<div className={`form-group common-autocomplete ${stationError ? 'red-border' : ''}`}>
					<div className='p-float-label input-wrapper'>
						<AirportAutocomplete
							autoCompleteType={AUTOCOMPLETE_TYPE.ORIGIN}
							selectedOrigin={selectedAirport}
							setSelectedOrigin={airport => {
								setSelectedAirport(airport)
								setStationError(null)
							}}
							clearError={() => setStationError(null)}
							handleSetError={(field, message) => setStationError(message)}
							draftCallback={() => { }}
						/>
						<label htmlFor={FORM_FIELDS.ORIGIN}>{t('COMMON.SPECIFY_STATION_CODE')}</label>
					</div>
					<span
						className='search-panel__errormsg slr-station-error'
						role={stationError ? 'alert' : undefined}
					>
						{stationError && t(stationError, { ns: 'errors' })}
					</span>
				</div>
			</CustomPopup>
		)
	}

	return hasUserDetails || (canSelectStation && stationCode) ? (
		<Switch>
			<Route exact path='/slr-bookings' component={SlrBookingList} />
			<Route
				exact
				path='/slr-bookings/view/:slrNumber'
				render={({ match, location }) => (
					<SlrBookingDetailsView
						slrNumber={decodeURIComponent(match.params.slrNumber)}
						customerCode={location.state?.customerCode || ''}
					/>
				)}
			/>
			<Route exact path='/slr-bookings/input' component={SlrBookingInputs} />
			<Route
				exact
				path='/slr-bookings/modify/:slrNumber'
				render={(props) => <SlrBookingInputs {...props} isEditMode={true} />}
			/>
		</Switch>
	) : (
		<ErrorPage />
	)
}

export default SlrBookingRoot
